import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { EQUIPMENT_STATUSES, formatDate } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Gauge, ShieldCheck, Loader2 } from "lucide-react";

export default function ScanView() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/public/equipment/${id}`)
      .then(({ data }) => setData(data))
      .catch(() => setError("Equipment not found. Please verify the QR code or work order number."));
  }, [id]);

  return (
    <div className="flex min-h-screen flex-col items-center bg-background px-4 py-12" data-testid="scan-view-page">
      <div className="mb-8 flex items-center gap-2">
        <Gauge className="h-7 w-7 text-primary" />
        <span className="font-display text-xl font-bold tracking-tight">BMDLabsystem</span>
        <span className="inline-flex items-center gap-1 rounded-sm border border-border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          <ShieldCheck className="h-3 w-3 text-emerald-500" /> ISO/IEC 17025
        </span>
      </div>

      {error && (
        <Card className="w-full max-w-md" data-testid="scan-error">
          <CardContent className="py-8 text-center text-sm text-red-500">{error}</CardContent>
        </Card>
      )}
      {!data && !error && <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />}

      {data && (
        <Card className="w-full max-w-md" data-testid="scan-result-card">
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="font-mono text-lg" data-testid="scan-work-order">{data.work_order_no}</CardTitle>
              <StatusBadge status={data.status} label={EQUIPMENT_STATUSES[data.status]} testid="scan-status-badge" />
            </div>
          </CardHeader>
          <CardContent>
            <dl className="space-y-3 text-sm">
              {[
                ["Equipment", data.name],
                ["Manufacturer", data.manufacturer],
                ["Model", data.model],
                ["Serial Number", data.serial_number],
                ["Owner", data.customer_company],
                ["Condition on Receipt", data.condition_on_receipt],
                ["Received On", formatDate(data.received_date)],
              ].map(([k, v]) => (
                <div key={k}>
                  <dt className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{k}</dt>
                  <dd className="mt-0.5" data-testid={`scan-field-${k.toLowerCase().replace(/ /g, "-")}`}>{v || "—"}</dd>
                </div>
              ))}
            </dl>
            <p className="mt-6 border-t border-border pt-3 text-center font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              Verified lab asset record · BMDLabsystem LIMS
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
